import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Copy, Power, PowerOff, ShieldCheck, ShieldOff, Lock, Eye, EyeOff } from "lucide-react";
import { formatDay } from "@/lib/format";
import { usePipelineCtx } from "@/lib/pipelineContext.jsx";
import { useTerm } from "@/lib/terminology.jsx";

export default function Gates() {
  const nav = useNavigate();
  const { pipelineId } = usePipelineCtx();
  const term = useTerm();
  const [gates, setGates] = useState([]);
  const [showHidden, setShowHidden] = useState(false);

  const load = () => {
    if (!pipelineId) return;
    api.get("/gates", { params: { pipeline_id: pipelineId } }).then(({ data }) => setGates(data));
  };
  useEffect(() => { load(); }, [pipelineId]); // eslint-disable-line react-hooks/exhaustive-deps

  const publish = async (g) => {
    if (g.is_hidden) {
      toast.error("Unhide this gate before publishing.");
      return;
    }
    if (!window.confirm("Publishing locks this gate's criteria and scoring. Name and description can still be edited. Confirm publish?")) return;
    try {
      await api.post(`/gates/${g.gate_id}/publish`);
      toast.success("Gate published");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };
  const unpublish = async (g) => {
    if (!window.confirm("Unpublishing removes this gate from the pickers for new Gate Runs and deactivates all its Gate-Stage mappings. Continue?")) return;
    try {
      const { data } = await api.post(`/gates/${g.gate_id}/unpublish`);
      toast.success(
        data.deactivated_mappings > 0
          ? `Gate unpublished · ${data.deactivated_mappings} mapping(s) deactivated`
          : "Gate unpublished"
      );
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };
  const toggle = async (g) => {
    await api.post(`/gates/${g.gate_id}/toggle`);
    toast.success(g.is_active ? "Gate disabled" : "Gate enabled");
    load();
  };
  const duplicate = async (g) => {
    const { data } = await api.post(`/gates/${g.gate_id}/duplicate`);
    toast.success("Gate duplicated");
    nav(`/gates/${data.gate_id}`);
  };
  const hide = async (g) => {
    await api.post(`/gates/${g.gate_id}/hide`);
    toast.success("Gate hidden");
    load();
  };
  const unhide = async (g) => {
    await api.post(`/gates/${g.gate_id}/unhide`);
    toast.success("Gate unhidden");
    load();
  };

  const visible = gates.filter((g) => showHidden || !g.is_hidden);

  return (
    <div className="p-8">
      <div className="flex justify-between items-start">
        <div>
          <div className="cw-caption uppercase tracking-widest">{term("Gate")} Frameworks</div>
          <h1 className="cw-h1 mt-1">Evaluation {term("Gates")}</h1>
          <p className="text-sm text-[var(--cw-muted)] mt-1">
            Structured evaluation frameworks with pass/fail, scored, or rubric criteria.
          </p>
        </div>
        <button onClick={() => nav("/gates/new")} className="cw-btn-primary flex items-center gap-2" data-testid="new-gate-btn">
          <Plus size={16} /> New {term("Gate")}
        </button>
      </div>

      <div className="mt-4">
        <label className="text-xs inline-flex items-center gap-2">
          <input type="checkbox" checked={showHidden}
            onChange={(e) => setShowHidden(e.target.checked)}
            data-testid="gates-show-hidden" />
          Show hidden {term("gates").toLowerCase()}
        </label>
      </div>

      <div className="mt-4 grid md:grid-cols-2 gap-4">
        {visible.map((g) => (
          <div key={g.gate_id} className="cw-card p-5"
            style={{ opacity: g.is_hidden ? 0.55 : 1 }}
            data-testid={`gate-card-${g.gate_name}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-semibold text-base">{g.gate_name}</h3>
                  {g.is_published ? (
                    <span className="cw-badge" style={{ background: "rgba(44,154,82,0.12)", color: "#1F6E3A" }}>
                      <Lock size={10} /> Published
                    </span>
                  ) : (
                    <span className="cw-badge" style={{ background: "rgba(224,161,0,0.12)", color: "#7A5900" }}>
                      Draft
                    </span>
                  )}
                  {g.is_hidden && (
                    <span className="cw-badge" style={{ background: "rgba(0,0,0,0.08)", color: "#666" }}>
                      Hidden
                    </span>
                  )}
                  {!g.is_active && (
                    <span className="cw-badge" style={{ background: "rgba(0,0,0,0.06)", color: "#666" }}>
                      Disabled
                    </span>
                  )}
                </div>
                <div className="text-xs text-[var(--cw-muted)] mt-1">
                  {g.gate_type === "weighted_scoring" ? "Weighted scoring" : "Hard filter"} · {g.criteria.length} criteria
                </div>
                {g.description && (
                  <p className="text-sm text-[var(--cw-muted)] mt-2 line-clamp-2">{g.description}</p>
                )}
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <button onClick={() => nav(`/gates/${g.gate_id}`)} className="cw-btn-ghost text-xs" data-testid={`open-gate-${g.gate_name}`}>
                {g.is_published ? "View / Edit name" : "Edit"}
              </button>
              {!g.is_published && !g.is_hidden && (
                <button onClick={() => publish(g)} className="cw-btn-primary text-xs inline-flex items-center gap-1" data-testid={`publish-gate-${g.gate_name}`}>
                  <ShieldCheck size={12} /> Publish
                </button>
              )}
              {g.is_published && (
                <button onClick={() => unpublish(g)} className="cw-btn-ghost text-xs inline-flex items-center gap-1" data-testid={`unpublish-gate-${g.gate_name}`}>
                  <ShieldOff size={12} /> Unpublish
                </button>
              )}
              <button onClick={() => duplicate(g)} className="cw-btn-ghost text-xs inline-flex items-center gap-1" data-testid={`duplicate-gate-${g.gate_name}`}>
                <Copy size={12} /> Duplicate
              </button>
              <button onClick={() => toggle(g)} className="cw-btn-ghost text-xs inline-flex items-center gap-1" data-testid={`toggle-gate-${g.gate_name}`}>
                {g.is_active ? <><PowerOff size={12} /> Disable</> : <><Power size={12} /> Enable</>}
              </button>
              {!g.is_published && !g.is_hidden && (
                <button onClick={() => hide(g)} className="cw-btn-ghost text-xs inline-flex items-center gap-1"
                  data-testid={`hide-gate-${g.gate_name}`}>
                  <EyeOff size={12} /> Hide
                </button>
              )}
              {g.is_hidden && (
                <button onClick={() => unhide(g)} className="cw-btn-ghost text-xs inline-flex items-center gap-1"
                  data-testid={`unhide-gate-${g.gate_name}`}>
                  <Eye size={12} /> Unhide
                </button>
              )}
            </div>
            <div className="cw-caption mt-3">Created {formatDay(g.created_at)}</div>
          </div>
        ))}
        {visible.length === 0 && (
          <div className="col-span-full cw-card p-10 text-center text-[var(--cw-muted)]">
            No gates yet. Click <strong>New Gate</strong> to create one.
          </div>
        )}
      </div>
    </div>
  );
}
